import React, { useEffect, useState } from "react";
import Spinner from "react-bootstrap/Spinner";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { sendPromoCode } from "../../../store/actions/PromoCodeActions";
import { getListWebUser } from "../../../store/actions/user-profile.actions";

const serviceTypes = ["JOB", "DISK", "PREMIUMSUB"];

const getPromoServiceTypes = (promo) => {
  if (!promo?.rules?.length) return serviceTypes;
  const activeRules = promo.rules.filter((r) => r.is_active !== false);
  const types = Array.from(new Set(activeRules.map((r) => r.service_type)));
  return types.length ? types : serviceTypes;
};

const SendPromoModal = ({ show, promo, onClose }) => {
  const dispatch = useDispatch();

  const [sendServiceType, setSendServiceType] = useState("DISK");
  const [userSearchText, setUserSearchText] = useState("");
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [isUserLoading, setIsUserLoading] = useState(false);
  const [isFetchingMoreUsers, setIsFetchingMoreUsers] = useState(false);
  const [userPage, setUserPage] = useState(1);
  const [hasMoreUsers, setHasMoreUsers] = useState(true);
  const [isSendSubmitting, setIsSendSubmitting] = useState(false);

  const normalizeUsers = (resPayload) => {
    if (!resPayload) return [];
    if (Array.isArray(resPayload)) return resPayload;
    if (Array.isArray(resPayload?.results)) return resPayload.results;
    if (Array.isArray(resPayload?.data)) return resPayload.data;
    return [];
  };

  const fetchUsersForSend = async (searchStr = "", page = 1, isLoadMore = false) => {
    if (isLoadMore) {
      setIsFetchingMoreUsers(true);
    } else {
      setIsUserLoading(true);
    }

    try {
      const res = await dispatch(
        getListWebUser({ pageParam: page, size: 20, search: searchStr })
      );
      const list = normalizeUsers(res?.payload);
      const hasNext = !!res?.payload?.next || list.length >= 20;

      if (isLoadMore) {
        setAvailableUsers((prev) => {
          const existingIds = new Set(prev.map((u) => u.id));
          const newUnique = list.filter((u) => !existingIds.has(u.id));
          return [...prev, ...newUnique];
        });
      } else {
        setAvailableUsers(list);
      }

      setUserPage(page);
      setHasMoreUsers(hasNext);
    } catch (err) {
      console.error("Error fetching users for send promo:", err);
    } finally {
      setIsUserLoading(false);
      setIsFetchingMoreUsers(false);
    }
  };

  const handleUserListScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (
      scrollHeight - scrollTop - clientHeight < 40 &&
      hasMoreUsers &&
      !isUserLoading &&
      !isFetchingMoreUsers
    ) {
      fetchUsersForSend(userSearchText, userPage + 1, true);
    }
  };

  useEffect(() => {
    if (!show || !promo) return;

    const availableTypes = getPromoServiceTypes(promo);
    setSendServiceType(availableTypes[0] || "DISK");
    setUserSearchText("");
    setSelectedUserIds([]);
    setUserPage(1);
    setHasMoreUsers(true);
  }, [show, promo]);

  // Debounce user search in Send Promo Code modal
  useEffect(() => {
    if (!show) return;

    const timer = setTimeout(() => {
      fetchUsersForSend(userSearchText, 1, false);
    }, 400);

    return () => clearTimeout(timer);
  }, [userSearchText, show]);

  const toggleUserSelection = (userId) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAllUsers = () => {
    if (selectedUserIds.length === availableUsers.length) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(availableUsers.map((u) => u.id));
    }
  };

  const handleSendSubmit = async (e) => {
    e.preventDefault();
    if (!promo?.id) return;
    if (!selectedUserIds.length) {
      toast.error("Please select at least one user to send the promo code.", {
        position: "top-right",
      });
      return;
    }

    setIsSendSubmitting(true);
    try {
      const payload = {
        user_ids: selectedUserIds,
        service_type: sendServiceType,
      };

      const res = await dispatch(
        sendPromoCode({
          promo_id: promo.id,
          body: payload,
        })
      );

      if (res?.payload?.success || res?.payload?.message) {
        toast.success(
          res?.payload?.message || "Promo notification queued successfully.",
          {
            position: "top-right",
            autoClose: 5000,
          }
        );
        onClose();
      } else {
        throw new Error(
          res?.payload?.message || "Failed to send promo code notification."
        );
      }
    } catch (error) {
      toast.error(
        error?.data?.message ||
          error?.data?.detail ||
          error?.message ||
          "Something went wrong while sending promo code!",
        {
          position: "top-right",
          autoClose: 5000,
        }
      );
    } finally {
      setIsSendSubmitting(false);
    }
  };

  if (!show || !promo) return null;

  return (
    <div
      className="modal fade show"
      style={{
        display: "block",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
      }}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="modal-dialog modal-dialog-centered modal-lg"
        style={{ maxWidth: "700px" }}
      >
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              Send Promo Code: <span className="text-primary font-w600">{promo.code}</span>
            </h5>
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={onClose}
            />
          </div>
          <form onSubmit={handleSendSubmit}>
            <div className="modal-body">
              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label font-w600">Service Type</label>
                  <select
                    name="service_type"
                    className="form-control"
                    value={sendServiceType}
                    onChange={(e) => setSendServiceType(e.target.value)}
                  >
                    {getPromoServiceTypes(promo).map((st) => (
                      <option key={st} value={st}>
                        {st}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-12 mt-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <label className="form-label font-w600 mb-0">
                      {`Select Users ${selectedUserIds?.length ? ` (${selectedUserIds?.length} selected)` : ''}`}
                    </label>
                    {availableUsers.length > 0 && (
                      <button
                        type="button"
                        className="btn btn-xs btn-outline-primary"
                        onClick={handleSelectAllUsers}
                      >
                        {selectedUserIds.length === availableUsers.length
                          ? "Deselect All"
                          : "Select All Loaded"}
                      </button>
                    )}
                  </div>

                  <div className="input-group mb-2">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search users by name or email..."
                      value={userSearchText}
                      onChange={(e) => {
                        setUserSearchText(e.target.value);
                      }}
                    />
                    {userSearchText && (
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => {
                          setUserSearchText("");
                        }}
                      >
                        Clear
                      </button>
                    )}
                  </div>

                  <div
                    className="border rounded p-2"
                    style={{ maxHeight: "250px", overflowY: "auto" }}
                    onScroll={handleUserListScroll}
                  >
                    {isUserLoading ? (
                      <div className="text-center py-3">
                        <Spinner animation="border" size="sm" variant="primary" />
                        <span className="ms-2">Loading users...</span>
                      </div>
                    ) : availableUsers.length ? (
                      <>
                        {availableUsers.map((user) => {
                          const isSelected = selectedUserIds.includes(user.id);
                          return (
                            <div
                              key={user.id}
                              className={`d-flex align-items-center justify-content-between p-2 rounded mb-1`}
                              style={{ cursor: "pointer" }}
                              onClick={() => toggleUserSelection(user.id)}
                            >
                              <div className="d-flex align-items-center gap-3">
                                <input
                                  type="checkbox"
                                  className="form-check-input mt-0"
                                  checked={isSelected}
                                  onChange={() => {}}
                                  style={{ cursor: "pointer" }}
                                />
                                <div>
                                  <div
                                    className="font-w600 text-dark"
                                    style={{ fontSize: "14px" }}
                                  >
                                    {user.first_name || user.last_name
                                      ? `${user.first_name || ""} ${user.last_name || ""}`.trim()
                                      : user.email || `User #${user.id}`}
                                  </div>
                                  <small className="text-muted">
                                    {user.email || `ID: ${user.id}`}
                                  </small>
                                </div>
                              </div>
                              {/* <span className="badge badge-sm badge-outline-secondary">
                                ID: {user.id}
                              </span> */}
                            </div>
                          );
                        })}
                        {isFetchingMoreUsers && (
                          <div className="text-center py-2">
                            <Spinner animation="border" size="sm" variant="primary" />
                            <span className="ms-2 small text-muted">
                              Loading more users...
                            </span>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-center py-3 text-muted">
                        No users found.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-light"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSendSubmitting || !selectedUserIds.length}
              >
                {isSendSubmitting
                  ? "Sending..."
                  : `Send Code to ${selectedUserIds.length===0?'':selectedUserIds.length} ${selectedUserIds.length === 1 ? "User" : "Users"}`}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SendPromoModal;
